import React, {useEffect} from 'react';

import {useStore} from 'effector-react';
import {HelmetProvider} from 'react-helmet-async';
import {Redirect, Route, Router, Switch} from 'react-router-dom';

import {Content} from 'components/common/Content';
import {Header} from 'components/common/Header';
import {PrivateRoute} from 'components/common/PrivateRoute';
import {MainSidebar} from 'components/common/Sidebar/MainSidebar';
import {MobileSidebar} from 'components/common/Sidebar/MobileSidebar';
import {LoginPage} from 'components/pages/Login';
import {NotFound} from 'components/pages/NotFound';
import {RegisterPage} from 'components/pages/Register';
import {ErrorBoundary} from 'components/ui-kit/ErrorBoundary';
import {authBroadcast} from 'core/broadcastAuth';
import {history} from 'core/history';
import {ROUTE, ROUTES_DATA} from 'core/routes';
import {$mainRole, isAuth$} from 'models/auth';

import {UserRoles} from 'types';

import {Background, GlobalStyle, ToastContainerRoot} from './styled';

export function App() {
    const isAuth = useStore(isAuth$);

    const role = useStore($mainRole);

    const redirectMapping: {DEFAULT: ROUTE} & Partial<Record<UserRoles, ROUTE>> = {
        DEFAULT: ROUTE.DASHBOARD,
        [UserRoles.ROLE_MPL_TS_DIRECTOR]: ROUTE.DECLARATIONS,
        [UserRoles.ROLE_MPL_TS_EMPLOYEE]: ROUTE.DECLARATIONS,
    };
    const redirect = redirectMapping[role ?? 'DEFAULT'] ?? redirectMapping.DEFAULT;

    useEffect(() => () => authBroadcast.close(), []);

    return (
        <>
            <GlobalStyle />
            <Router history={history}>
                <HelmetProvider>
                    <ErrorBoundary>
                        {isAuth && <Header />}
                        {isAuth && <MobileSidebar />}
                        {isAuth && <MainSidebar />}
                        <Switch>
                            <Route path="/">
                                <ToastContainerRoot
                                    position="bottom-right"
                                    autoClose={5000}
                                    hideProgressBar
                                    closeOnClick
                                />
                                <Switch>
                                    <Route path="/login" exact>
                                        <LoginPage />
                                    </Route>
                                    <Route path="/register" exact>
                                        <RegisterPage />
                                    </Route>
                                    {Object.values(ROUTE).map((elem) => {
                                        const data = ROUTES_DATA[elem];
                                        return (
                                            data && (
                                                <PrivateRoute
                                                    key={elem}
                                                    path={elem}
                                                    exact={data.exact}
                                                    permissions={data.permissions}
                                                    permissionAccessRoles={
                                                        data.permissionAccessRoles
                                                    }
                                                    roles={data.roles}
                                                >
                                                    <Content flex={data.flex}>
                                                        {data.component}
                                                    </Content>
                                                </PrivateRoute>
                                            )
                                        );
                                    })}
                                    <Route path="/" exact>
                                        <Redirect to={redirect} />
                                    </Route>
                                    <Route path="*" exact>
                                        <Content>
                                            <NotFound />
                                        </Content>
                                    </Route>
                                </Switch>
                            </Route>
                        </Switch>
                    </ErrorBoundary>
                </HelmetProvider>
            </Router>
            <Background />
        </>
    );
}
